import type { Priority, TaskStatus } from './salesApi';

/**
 * The five columns of the seller's board, left to right. `DONE` and `DROPPED`
 * are terminal; `DROPPED` is a column rather than a delete so conversion stays
 * visible. Ticket 7.5 pins a «Новые заявки» lane in front of these.
 */
export const STATUS_COLUMNS: { id: TaskStatus; color: string }[] = [
  { id: 'QUEUE', color: 'gray' },
  { id: 'IN_PROGRESS', color: 'yellow' },
  { id: 'WAITING', color: 'blue' },
  { id: 'DONE', color: 'green' },
  { id: 'DROPPED', color: 'red' },
];

/** The registry verdict as a coloured word (ticket 7.5). */
export const VERDICT_CLASSES: Record<'verified' | 'name_mismatch' | 'not_affiliated' | 'unavailable', string> = {
  verified: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  name_mismatch: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  not_affiliated: 'bg-destructive/10 text-destructive',
  unavailable: 'bg-muted text-muted-foreground',
};

/** Whole days since an ISO instant, never negative. */
export function daysSince(iso: string, now: Date = new Date()): number {
  const ms = now.getTime() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

export const TERMINAL_STATUSES: TaskStatus[] = ['DONE', 'DROPPED'];

export const PRIORITY_CLASSES: Record<Priority, string> = {
  P1: 'bg-primary text-primary-foreground',
  P2: 'bg-primary/10 text-primary dark:bg-primary/20',
  P3: 'border border-border text-muted-foreground',
};

/** Stable avatar colours per member: the same person always gets the same tint. */
const AVATAR_CLASSES = [
  'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
  'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  'bg-lime-100 text-lime-800 dark:bg-lime-900/40 dark:text-lime-200',
];

export function avatarClass(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_CLASSES[hash % AVATAR_CLASSES.length];
}

export function initials(name: string): string {
  const parts = name.trim().split(/[\s-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Period presets, mirroring the archive filter's identifiers. */
export const PERIOD_PRESETS = [
  'today',
  'yesterday',
  'currentWeek',
  'previousWeek',
  'currentMonth',
  'previousMonth',
] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

/**
 * `@db.Date` columns come back from the API as a full ISO instant
 * (`2026-10-09T00:00:00.000Z`). Everything on screen wants the calendar day,
 * and a `<input type="date">` only accepts that form.
 */
export function dateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '';
}


/**
 * `YYYY-MM-DD` in Estonian time. The server resolves every period itself; this
 * only decides which dates the pickers start on, so the seller opening the page
 * from another timezone still sees the Estonian "today".
 */
export function tallinnToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Tallinn' }).format(now);
}

function shift(day: string, days: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function presetRange(preset: PeriodPreset, today = tallinnToday()): [string, string] {
  const d = new Date(`${today}T00:00:00Z`);
  switch (preset) {
    case 'today':
      return [today, today];
    case 'yesterday': {
      const y = shift(today, -1);
      return [y, y];
    }
    case 'currentWeek':
    case 'previousWeek': {
      const dow = d.getUTCDay();
      const monday = shift(today, dow === 0 ? -6 : 1 - dow);
      const start = preset === 'currentWeek' ? monday : shift(monday, -7);
      return [start, shift(start, 6)];
    }
    case 'currentMonth':
    case 'previousMonth': {
      const y = d.getUTCFullYear();
      const m = d.getUTCMonth() + (preset === 'currentMonth' ? 0 : -1);
      const start = new Date(Date.UTC(y, m, 1));
      const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0));
      return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
    }
  }
}
